import {
  CaptionedImage,
  DetailsPanel,
  GenderIcon,
  LoadingIcon,
} from '@/components/controls';
import { useNichePersonImage } from '@/hooks';
import { toProperCase } from '@/utils';
import {
  EditDocument as EditDocumentIcon,
  Map as MapIcon,
  Verified as VerifiedIcon,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Card,
  CardHeader,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { differenceInYears, format } from 'date-fns';
import { Coffin as CoffinIcon } from 'mdi-material-ui';

export function SuddenDeath({ incident, onShowMapClick }) {
  const { occurrence, deceased } = incident;
  const {
    query: { data: imageSrc, isFetching },
  } = useNichePersonImage(deceased?.id);

  return (
    <Card variant="outlined">
      <CardHeader
        avatar={
          <Avatar>
            <CoffinIcon />
          </Avatar>
        }
        action={
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
            <LoadingIcon isLoading={isFetching} />
            {occurrence?.address && (
              <Tooltip title="Show map">
                <IconButton size="small" onClick={onShowMapClick}>
                  <MapIcon sx={{ fontSize: 'inherit' }} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        }
        title={occurrence?.number}
        subheader={
          occurrence?.reportedTime &&
          format(occurrence.reportedTime, 'dd/MM/yyyy HH:mm')
        }
      />
      {deceased?.id && (
        <CaptionedImage
          imageSrc={imageSrc}
          fallbackHeight={240}
          width="100%"
          borderRadius={0}
          topCaption={
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Box>
                {toProperCase(
                  [deceased?.forenames, deceased?.surname]
                    .filter(Boolean)
                    .join(' '),
                )}
              </Box>
              <GenderIcon gender={deceased?.gender} />
            </Stack>
          }
          bottomCaption={
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
              <Typography>
                {format(deceased.dateOfBirth, 'dd/MM/yyyy')}
              </Typography>
              <Typography sx={{ color: 'primary.main' }}>
                ({differenceInYears(new Date(), deceased.dateOfBirth)})
              </Typography>
            </Stack>
          }
        />
      )}
      <Stack spacing={1} sx={{ p: 1 }}>
        <DetailsPanel
          title="Owned by"
          entries={[
            {
              label: 'OIC',
              value: occurrence?.officerInCase?.collarNumber,
            },
            {
              label: 'Team',
              value: occurrence?.officerInCase?.team,
            },
          ]}
        />
        <Stack
          direction="row"
          spacing={1}
          sx={{ p: 1, justifyContent: 'flex-end' }}
        >
          <Tooltip
            title={
              incident?.latestSupervisorEndorsementTime ? (
                <Typography sx={{ fontSize: 11 }}>
                  Last endorsed by supervisor @{' '}
                  {format(
                    incident.latestSupervisorEndorsementTime,
                    'dd/MM/yyyy HH:mm',
                  )}
                </Typography>
              ) : (
                'Not yet endorsed by supervisor'
              )
            }
            placement="top-end"
            arrow
          >
            <VerifiedIcon
              color={
                incident?.latestSupervisorEndorsementTime ? 'success' : 'error'
              }
              sx={{ fontSize: 16 }}
            />
          </Tooltip>
          <Tooltip
            title={
              incident?.suddenDeathDocumentationCompleted
                ? 'Documentation complete'
                : 'Documentation outstanding'
            }
            placement="top-end"
            arrow
          >
            <EditDocumentIcon
              color={
                incident?.suddenDeathDocumentationCompleted
                  ? 'success'
                  : 'error'
              }
              sx={{ fontSize: 16 }}
            />
          </Tooltip>
        </Stack>
      </Stack>
    </Card>
  );
}
